import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

export default class extends Controller {
  static targets = [
    'loading',
    'dailyEnrollmentsCanvas', 'dayOfWeekCanvas',
    'totalUsersValue', 'biometricEnabledValue', 'newEnrollmentsValue', 'enrollmentRateValue'
  ]

  static values = {
    url: String,
    startDate: String,
    endDate: String
  }

  connect() {
    this.charts = {}
    this.isLoading = false
    this.loadData()
  }

  disconnect() {
    this.destroyAllCharts()
  }

  destroyAllCharts() {
    Object.keys(this.charts).forEach((name) => {
      if (this.charts[name]) {
        this.charts[name].destroy()
        this.charts[name] = null
      }
    })
    this.charts = {}
  }

  updateDateRange(event) {
    const { startDate, endDate } = event.detail
    this.startDateValue = startDate
    this.endDateValue = endDate
    this.loadData()
  }

  async loadData() {
    if (this.isLoading) return
    this.isLoading = true
    this.showLoading()

    try {
      const params = new URLSearchParams({
        start_date: this.startDateValue,
        end_date: this.endDateValue
      })

      const response = await fetch(`${this.urlValue}?${params}`)
      const data = await response.json()

      this.destroyAllCharts()
      this.updateStats(data.stats)
      this.updateCharts(data)
    } catch (error) {
      console.error('Failed to load biometric analytics data:', error)
    } finally {
      this.isLoading = false
      this.hideLoading()
    }
  }

  updateStats(stats) {
    if (this.hasTotalUsersValueTarget) {
      this.totalUsersValueTarget.textContent = this.formatNumber(stats.total_users)
    }
    if (this.hasBiometricEnabledValueTarget) {
      this.biometricEnabledValueTarget.textContent = this.formatNumber(stats.biometric_enabled)
    }
    if (this.hasNewEnrollmentsValueTarget) {
      this.newEnrollmentsValueTarget.textContent = this.formatNumber(stats.new_enrollments)
    }
    if (this.hasEnrollmentRateValueTarget) {
      this.enrollmentRateValueTarget.textContent = `${stats.enrollment_rate}%`
    }
  }

  updateCharts(data) {
    this.createDailyEnrollmentsChart(data.daily_enrollments)
    this.createDayOfWeekChart(data.enrollment_by_day_of_week)
  }

  createDailyEnrollmentsChart(dailyData) {
    if (!this.hasDailyEnrollmentsCanvasTarget) return

    const labels = Object.keys(dailyData).sort()
    const values = labels.map((date) => dailyData[date])

    this.charts.dailyEnrollments = new Chart(this.dailyEnrollmentsCanvasTarget, {
      type: 'line',
      data: {
        labels: labels.map((d) => this.formatDate(d)),
        datasets: [{
          label: 'Enrollments',
          data: values,
          borderColor: '#171717',
          backgroundColor: 'rgba(23, 23, 23, 0.1)',
          borderWidth: 2,
          fill: true,
          tension: 0.3
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { precision: 0 }
          }
        }
      }
    })
  }

  createDayOfWeekChart(dayData) {
    if (!this.hasDayOfWeekCanvasTarget) return

    const labels = Object.keys(dayData)
    const values = Object.values(dayData)

    this.charts.dayOfWeek = new Chart(this.dayOfWeekCanvasTarget, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Enrollments',
          data: values,
          backgroundColor: '#171717',
          borderColor: '#171717',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { precision: 0 }
          }
        }
      }
    })
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  formatNumber(num) {
    if (num === undefined || num === null) return '-'
    return num.toLocaleString()
  }

  formatDate(dateStr) {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
}
